import React from 'react';
import { onActivateKey } from '../../utils/a11y';
import { FONT_SIZE, FONT_WEIGHT, SPACING } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';

export interface TabBarItem<K extends string = string> {
  key:   K;
  label: React.ReactNode;
}

interface AppTabBarProps<K extends string> {
  tabs:     TabBarItem<K>[];
  active:   K;
  onChange: (key: K) => void;
  /** 탭 바 아래 여백. 기본 SPACING.lg. */
  gap?:     number;
  style?:   React.CSSProperties;
}

/**
 * 페이지 상단 모드 전환용 밑줄 탭 바(2026-08-31 공통화) — `CardManagementPage`(가격/재고)와
 * `ProductRegistrationPage`(확장팩 일괄/검색 개별)가 바이트 단위로 같은 `PageModeTabs`를
 * 각자 갖고 있던 걸 하나로 모은다. `TabStat`(라벨+건수 pill, 필터 바 전용)과 달리 여기엔
 * 건수가 없고 단순 라벨만 — 성격이 달라 별도 컴포넌트로 둔다.
 */
export function AppTabBar<K extends string>({ tabs, active, onChange, gap = SPACING.lg, style }: AppTabBarProps<K>) {
  const t = useThemeTokens();

  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', minHeight: 44,
      marginBottom: gap, borderBottom: `1px solid ${t.border}`, ...style,
    }}>
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <div
            key={tab.key}
            role="button"
            tabIndex={0}
            className="app-focus-ring"
            aria-pressed={isActive}
            onClick={() => onChange(tab.key)}
            onKeyDown={onActivateKey(() => onChange(tab.key))}
            style={{
              display: 'flex', alignItems: 'center', padding: `0 ${SPACING.md}px`, marginBottom: -1,
              cursor: 'pointer', fontSize: FONT_SIZE.md,
              fontWeight: isActive ? FONT_WEIGHT.semibold : FONT_WEIGHT.regular,
              color: isActive ? t.accent : t.textSecondary,
              borderBottom: `2px solid ${isActive ? t.accent : 'transparent'}`,
              userSelect: 'none',
            }}
          >
            {tab.label}
          </div>
        );
      })}
    </div>
  );
}
